import React, { useEffect, useState } from 'react';
import './GraphAnimations.css';

interface GraphStatusPillProps {
  /** Whether this pill should be visible */
  visible: boolean;
  /** Message to display */
  message: string;
  /** Visual variant */
  variant: 'fetch' | 'expand';
  /** Dark mode */
  isDarkMode: boolean;
  /** Offset from top (to stack multiple pills) */
  topOffset?: number;
}

/**
 * A polished floating status pill for data-fetching and node-expansion operations.
 * Slides in from the right with a spring animation, and slides out when dismissed.
 */
const GraphStatusPill: React.FC<GraphStatusPillProps> = ({
  visible,
  message,
  variant,
  isDarkMode,
  topOffset = 20,
}) => {
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setExiting(false);
    } else if (mounted) {
      // Trigger exit animation, then unmount
      setExiting(true);
      const timer = setTimeout(() => {
        setMounted(false);
        setExiting(false);
      }, 300); // match pill-exit duration
      return () => clearTimeout(timer);
    }
  }, [visible]);

  if (!mounted) return null;

  const mode = isDarkMode ? 'dark-mode' : 'light-mode';

  return (
    <div
      className={`graph-status-pill ${mode} ${variant} ${exiting ? 'exit' : ''}`}
      style={{ top: topOffset }}
    >
      <div className={`graph-pill-spinner ${variant}`} />
      <span className="graph-pill-text">{message}</span>
    </div>
  );
};

export default GraphStatusPill;
